import {Component, Input, OnChanges, OnInit} from '@angular/core';
import {CountryChartData} from '../../../../models/country-chart-data.interface';
import {Chart} from 'angular-highcharts';
import {PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {RETURN_BY_YEAR} from '../../../../../constants/charts';
import {MessageService} from '../../../../../shared/services/message.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../../core/services/auth.service';
import {formatNumber} from '../../../../utils/number-format-utils';

interface YearCountryData {
  year: string;
  countryData: CountryChartData[];
}

@Component({
  selector: 'app-by-year-bar-chart',
  templateUrl: './by-year-bar-chart.component.html',
  styleUrls: ['./by-year-bar-chart.component.scss']
})
export class ByYearBarChartComponent implements OnInit, OnChanges {

  @Input()
  data!: [CountryChartData[]];
  @Input()
  displayUnits!: string;
  @Input()
  title!: string;
  @Input()
  subtitle!: string;
  @Input()
  countries!: string[];

  textBelow!: string;
  chart!: Chart;
  chartData!: SeriesOptionsType[];
  countryCodes!: string[];
  textBelowCode = 'kfcharts.Country.ReturnRate.ByYear.textBelow';
  isAdmin!: boolean;

  constructor(private readonly messageService: MessageService,
              private readonly router: Router,
              private authService: AuthService) {
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.getUserRoles().includes('WF_ADMIN');
    this.messageService.getDialogInfo(this.textBelowCode).subscribe((data) => {
      if (data !== null) {
        this.textBelow = data.description;
      }
    });
  }

  ngOnChanges(): void {
    if (this.data) {
      this.countryCodes = this.countries;
      const normalizedData = this.normalizeData(this.groupByYear(this.data), this.countryCodes);

      // highcharts
      this.chartData = this.convertToChartData(normalizedData);

      this.countryCodes = normalizedData[0].countryData.map(item => item.countryCode);
      this.chart = new Chart({
        chart: {
          zoomType: 'xy',
          type: 'column',
          options3d: {
            enabled: true,
            alpha: 7,
            beta: 0,
            depth: 35,
            viewDistance: 15
          }
        },
        colors: RETURN_BY_YEAR,
        title: {
          text: this.title
        },
        subtitle: {
          text: this.subtitle
        },
        exporting: {
          tableCaption: this.title + ' ' + this.subtitle
        },
        tooltip: {
          formatter(): string[]|undefined {
            if (this.point.name) {
              const pointOptions = this.point as PointOptionsObject;
              if (pointOptions.custom?.absolute === 0) {
                return [this.point.name];
              }
              return ['<div class="center"><b>' + pointOptions.custom?.title + '</b></div><br><br>',
                '- Put On Market: ' + formatNumber(pointOptions.custom?.absolute) + ' tons' + '<br>',
                '- Collected: ' + formatNumber(pointOptions.custom?.absoluteCollected) + ' tons' + '<br>',
              ];
            }
            // If not null, use the default formatter
            // return tooltip.defaultFormatter.call(this, tooltip);
            return undefined;
          }
        },
        xAxis: {
          categories: this.countryCodes,
          title: {
            text: null
          }
        },
        yAxis: {
          labels: {
            format: '{value}%'
          }
        },
        plotOptions: {
          column: {
            minPointLength: 0.5,
            dataLabels: {
              enabled: true,
              format: '{point.y:.2f}%'
            }
          },
        },
        series: this.chartData
      });
    }
  }

  editTextBelow(): void {
    this.router.navigate(
      ['/admin/chart-labels'],
      {state: { code: this.textBelowCode }}).then(
      () => {
        // do nothing
      }
    );
  }

  private normalizeData(groupedByYear: YearCountryData[], countryCodes: string[]): YearCountryData[] {
    const normalizedData: YearCountryData[] = [];
    groupedByYear.forEach(item => {
      if (item.countryData?.length === countryCodes?.length) {
        normalizedData.push(item);
      } else {
        const newCountryData: CountryChartData[] = [];
        countryCodes?.forEach(code => {
          const countryDataItem = item.countryData.find(c => c.countryCode === code);
          if (countryDataItem?.absolute && countryDataItem?.absoluteCollected) {
            newCountryData.push(countryDataItem);
          }
        });
        normalizedData.push({year: item.year, countryData: newCountryData});
      }
    });
    return normalizedData;
  }

  private convertToChartData(data: YearCountryData[]): SeriesOptionsType[] {
    this.chart?.ref?.hideData();
    const chartData = [] as SeriesOptionsType[];
    data.forEach(item => {
      if (this.router.url.includes('order=')) {
        item.countryData.sort((a, b) => {
          if ((a?.absoluteCollected ? (a?.absoluteCollected / a.absolute) * 100 : a.absolute) < (b?.absoluteCollected ? (b?.absoluteCollected / b.absolute) * 100 : b.absolute)) {
            if (this.router.url.includes('order=asc')) {
              return -1;
            }
            else {
              return 1;
            }
          }
          if ((a?.absoluteCollected ? (a?.absoluteCollected / a.absolute) * 100 : a.absolute) > (b?.absoluteCollected ? (b?.absoluteCollected / b.absolute) * 100 : b.absolute))  {
            if (this.router.url.includes('order=asc')) {
              return 1
            }
            else {
              return -1;
            }
          }
          return 0;
        });
      }
      chartData.push({
        name: item.year,
        type: 'column',
        data: item.countryData.map(dataItem => {
          if (dataItem?.absoluteCollected && (dataItem?.absoluteCollected / dataItem.absolute) * 100 <= 300) {
            return {
              y: Number(
                (dataItem?.absoluteCollected ? (dataItem?.absoluteCollected / dataItem.absolute) * 100 : dataItem.absolute).toFixed(2)),
              name: dataItem.countryName,
              custom: {
                absolute: dataItem.absolute,
                absoluteCollected: dataItem.absoluteCollected,
                countryCode: dataItem.countryCode,
                title: dataItem.countryName + ' - ' + dataItem.year,
              }
            };
          }
          else {
            return null;
          }
        })
      });
    });
    return chartData;
  }

  private groupByYear(objectArray: Array<any>): YearCountryData[] {
    const yearCountryDataArray: YearCountryData[] = [];
    const groupedArray = objectArray.reduce((acc, obj) => {
      const key = obj[0].year;
      if (!acc[key]) {
        acc[key] = [];
      }
      // Add object to list for given key's value
      obj.forEach((item: any) => acc[key].push(item));
      return acc;
    }, {});

    Object.getOwnPropertyNames(groupedArray).forEach((key: string) => {
      const yearCountryData = {} as YearCountryData;
      yearCountryData.year = key;
      yearCountryData.countryData = groupedArray[key];
      yearCountryDataArray.push(yearCountryData);
    });

    return yearCountryDataArray;
  }

}
